/**
 * Pure helper functions for computing oxygen equipment type display values on
 * the SWO (Standard Written Order). Extracted so they can be unit-tested
 * independently of the React PDF renderer.
 *
 * IMPORTANT: the formItemName string literals here must stay in sync with the
 * database `form_items.name` values. If those names change, update this file.
 */

type OxygenLineItem = {
  formItemName: string;
  formItemOptions: string[];
};

/**
 * Returns the stationary equipment type label for an oxygen SWO row.
 *
 * A stationary home concentrator is included for every usage type that
 * involves resting or sleeping O2 need: Nocturnally, Continuous (24 hours),
 * and With Exercise (exercise-use patients also receive a stationary
 * unit — see OxygenForm/constants.ts: "Stationary and portable").
 * The legacy "Continuous" form item used an explicit Stationary option instead.
 */
export const getOxygenStationaryType = (
  lineItems: OxygenLineItem[],
): "Home concentrator" | "None" => {
  const hasStationary = lineItems.some(
    (li) =>
      li.formItemName === "Nocturnally" ||
      li.formItemName === "Continuous (24 hours)" ||
      li.formItemName === "With Exercise" ||
      (li.formItemName === "Continuous" &&
        li.formItemOptions.includes("Stationary")),
  );
  return hasStationary ? "Home concentrator" : "None";
};

/**
 * Returns the portable equipment type label for an oxygen SWO row.
 *
 * Portable equipment is suppressed when the patient is explicitly not mobile
 * in the home (oxygenPatientMobileInHome === false). Otherwise it is included
 * whenever the usage type implies portability: With Exercise, Continuous (24
 * hours) or the legacy Continuous with a Portable option.
 *
 * @param oxygenPortableType - The specific portable type selected by the
 *   clinician (e.g. "Portable concentrator"). Falls back to the generic label
 *   for historical orders that predate the portable type field.
 */
export const getOxygenPortableType = (
  lineItems: OxygenLineItem[],
  oxygenPatientMobileInHome: boolean | null | undefined,
  oxygenPortableType: string | null | undefined,
): string => {
  if (oxygenPatientMobileInHome === false) return "None";

  const hasPortable = lineItems.some(
    (li) =>
      li.formItemName === "With Exercise" ||
      li.formItemName === "Continuous (24 hours)" ||
      (li.formItemName === "Continuous" &&
        li.formItemOptions.includes("Portable")),
  );

  if (!hasPortable) return "None";
  return oxygenPortableType || "Homefill system OR Portable Concentrator";
};
